import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { useAuthStore } from './store/auth';
import { useAuth } from './hooks/useAuth';
import { useThemeStore } from './store/theme';
import { LoginPage } from './pages/LoginPage';
import { SignupPage } from './pages/SignupPage';
import { Layout } from './components/common/Layout';
import { HomePage } from './pages/HomePage';
import { ScoutFormPage } from './pages/ScoutFormPage';
import { AnalyticsDashboardPage } from './pages/AnalyticsDashboardPage';
import { AllianceToolPage } from './pages/AllianceToolPage';
import { ExportPage } from './pages/ExportPage';
import { BrowseDataPage } from './pages/BrowseDataPage';
import './styles/globals.css';

const ProtectedRoute: React.FC<{ children: React.ReactNode; requiredRole?: string[] }> = ({
  children,
  requiredRole,
}) => {
  const { isAuthenticated, user } = useAuth();

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (requiredRole && user && !requiredRole.includes(user.role)) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
};

export const App: React.FC = () => {
  const { initialize } = useAuthStore();
  const { isAuthenticated } = useAuth();
  const { theme, initialize: initializeTheme } = useThemeStore();

  useEffect(() => {
    initialize();
    initializeTheme();
  }, [initialize, initializeTheme]);

  useEffect(() => {
    if (theme) {
      document.documentElement.setAttribute('data-theme', theme);
    }
  }, [theme]);

  return (
    <Router>
      <Routes>
        {/* Public routes */}
        <Route path="/login" element={<LoginPage />} />
        <Route path="/signup" element={<SignupPage />} />
        <Route path="/browse" element={<BrowseDataPage />} />

        {/* Protected routes */}
        <Route
          path="/*"
          element={
            <ProtectedRoute>
              <Layout>
                <Routes>
                  <Route path="/" element={<HomePage />} />
                  <Route
                    path="/scout"
                    element={
                      <ProtectedRoute requiredRole={['Admin', 'Scout']}>
                        <ScoutFormPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/analytics"
                    element={
                      <ProtectedRoute requiredRole={['Admin', 'Scout', 'Strategist']}>
                        <AnalyticsDashboardPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/alliance"
                    element={
                      <ProtectedRoute requiredRole={['Admin', 'Strategist']}>
                        <AllianceToolPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/export"
                    element={
                      <ProtectedRoute requiredRole={['Admin']}>
                        <ExportPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route path="/browse" element={<BrowseDataPage />} />
                </Routes>
              </Layout>
            </ProtectedRoute>
          }
        />
      </Routes>
    </Router>
  );
};

export default App;
